import { QBox, QFlex, QSpacer } from '@qualio/ui-components';
import { MultiRootEditor } from 'ckeditor5';
import { useEffect, useState } from 'react';
import { useStickyOutlinePanel } from '../../../../hooks/useStickyOutlinePanel';
import { QualioDocumentSection } from '../../../../api/model/document';
import { ChangeControlSection } from '../../../../types/DocumentCreate';
import { EditorName } from '../../../../config/handlers/EditorEventsHandlers';
import { TableOfContents } from '../../TableOfContents/TableOfContents';
import { BackToTop } from '../../BackToTop';
import styles from './QMultiRootEditor.module.css';

// Below this, the ToC column has been squeezed too narrow for its text to
// stay legible (even with ellipsis truncation) -- swap to the icon button.
// Above it, the column keeps shrinking continuously with the available
// space via flexbox (flexShrink + minWidth: 0 on .document-toc-container).
const EXTENDED_TOC_MIN_WIDTH = 180;

type OutlineSidebarProps = {
  editor: MultiRootEditor | null;
  scrollBoundaryRef: React.RefObject<HTMLElement>;
  toolbarElementId: string;
  outlineClassSuffix: string;
  sections: QualioDocumentSection[] | ChangeControlSection[];
  sectionTitlesVisible: boolean;
  editorName?: EditorName;
};

export const OutlineSidebar = ({
  editor,
  scrollBoundaryRef,
  toolbarElementId,
  outlineClassSuffix,
  sections,
  sectionTitlesVisible,
  editorName,
}: OutlineSidebarProps) => {
  const { tocContainerRef, outlineContainerRef } =
    useStickyOutlinePanel(toolbarElementId);
  const [showExtendedToc, setShowExtendedToc] = useState(true);

  useEffect(() => {
    const tocContainer = tocContainerRef.current;
    if (!tocContainer) {
      return;
    }
    const tocObserver = new ResizeObserver(([entry]) =>
      setShowExtendedToc(entry.contentRect.width > EXTENDED_TOC_MIN_WIDTH),
    );
    tocObserver.observe(tocContainer);
    return () => tocObserver.disconnect();
  }, [tocContainerRef]);

  return (
    <QFlex
      direction="column"
      flexShrink={1}
      flexGrow={1}
      minWidth={0}
      className={`${styles['document-toc-container']}`}
      ref={tocContainerRef}
    >
      <QBox
        ref={outlineContainerRef}
        data-cy={`document-outline-panel-${outlineClassSuffix}`}
      >
        <QBox
          className={`${
            editor && showExtendedToc ? '' : 'ck-hidden'
          } document-outline-container-${outlineClassSuffix}`}
        ></QBox>
        {editor && (
          <QBox className={showExtendedToc ? 'ck-hidden' : ''}>
            <TableOfContents
              showSubHeadings
              sections={sections}
              sectionTitlesVisible={sectionTitlesVisible}
              editorName={editorName}
            />
          </QBox>
        )}
      </QBox>
      <QSpacer height="100%" />
      <BackToTop parentRef={scrollBoundaryRef} />
    </QFlex>
  );
};
